import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import MarkdownText from './ui/MarkdownText';
import StatusBadge from './ui/StatusBadge';
import Icon from './Icon';
import { MetricCard } from './MetricCards';
import '../styles/components/ProjectTimeline.css';
import { ProjectEvent, ProjectEventType } from '../types/Planner';
import { loadProjectEvents, saveProjectEvents } from '../services/ProjectEventsService';
import { EVENTS_CHANGED_EVENT } from '../services/EventsService';
import { RunOfShowItem, refreshAllRunOfShowFromApi, loadAllRunOfShow } from '../services/RunOfShowService';
import { timelineStatusColors } from '../constants/statusColors';
import {
    scheduleCategoryColor,
    scheduleCategoryLabel,
    HIGH_PRIORITY_SCHEDULE_CATEGORIES,
} from '../constants/scheduleCategories';

interface ProjectTimelineProps {
    view?: 'gantt' | 'sequence' | 'run-of-show';
    onViewChange?: (view: 'gantt' | 'sequence' | 'run-of-show') => void;
    rosEventFilter?: string;
    onRosEventTitlesChange?: (titles: string[]) => void;
}

interface AggregatedScheduleItem extends RunOfShowItem {
  eventTitle: string;
  eventColor: string;
}

// Programme times arrive as "HH:MM" (24h) from RunOfShowService. Anything before
// 06:00 belongs to the small hours *after* the day's events (the after party
// runs to 2 AM), so it is rolled onto the following calendar day. Every
// position on the chart is computed in these rolled-over minutes.
const AFTER_MIDNIGHT_CUTOFF_MINS = 6 * 60;
const MINS_PER_DAY = 24 * 60;
const MIN_WINDOW_MINS = 4 * 60;
const DEFAULT_ITEM_MINS = 15;   // width for an item with no end time

const toMinutes = (t: string | undefined): number => {
    if (!t) return 0;
    const [h, m] = t.split(':').map(Number);
    if (Number.isNaN(h)) return 0;
    const total = h * 60 + (Number.isNaN(m) ? 0 : m);
    return total < AFTER_MIDNIGHT_CUTOFF_MINS ? total + MINS_PER_DAY : total;
};

/** End minutes, rolled forward when the item or event crosses midnight. */
const toEndMinutes = (start: string, end: string | undefined, fallback = DEFAULT_ITEM_MINS): number => {
    const s = toMinutes(start);
    if (!end) return s + fallback;
    let e = toMinutes(end);
    while (e <= s) e += MINS_PER_DAY;
    return e;
};

interface TimeWindow { start: number; end: number }

const formatMinutes = (mins: number): string => {
    const h24 = Math.floor(mins / 60) % 24;
    const m = mins % 60;
    const period = h24 < 12 ? 'AM' : 'PM';
    const display = h24 % 12 === 0 ? 12 : h24 % 12;
    return `${display}:${m.toString().padStart(2, '0')} ${period}`;
};

const formatHourTick = (mins: number): string => {
    const h24 = Math.floor(mins / 60) % 24;
    if (h24 === 0) return '12 AM';
    if (h24 < 12) return `${h24} AM`;
    if (h24 === 12) return '12 PM';
    return `${h24 - 12} PM`;
};

const EVENT_COLORS: Record<ProjectEventType, string> = {
    'getting-ready': '#a78bfa',
    'ceremony': '#f472b6',
    'cocktail-hour': '#34d399',
    'reception': '#60a5fa',
    'after-party': '#fb923c',
    'rehearsal': '#94a3b8',
    'rehearsal-dinner': '#fbbf24',
    'photography': '#e879f9',
};

const EVENT_LABELS: Record<ProjectEventType, string> = {
    'getting-ready': 'Getting Ready',
    'ceremony': 'Ceremony',
    'cocktail-hour': 'Cocktail Hour',
    'reception': 'Reception',
    'after-party': 'After Party',
    'rehearsal': 'Rehearsal',
    'rehearsal-dinner': 'Rehearsal Dinner',
    'photography': 'Photography',
};

/** Position of a moment within the chart window, as a percentage. */
const percentOf = (mins: number, win: TimeWindow) =>
    ((mins - win.start) / (win.end - win.start)) * 100;

const formatDisplayTime = (t: string) => formatMinutes(toMinutes(t) % MINS_PER_DAY);

const statusColor = timelineStatusColors;

const ProjectTimeline: React.FC<ProjectTimelineProps> = ({
    view = 'gantt',
    onViewChange,
    rosEventFilter: externalRosFilter,
    onRosEventTitlesChange,
}) => {
    const [events, setEvents] = useState<ProjectEvent[]>([]);
    const [selectedEvent, setSelectedEvent] = useState<ProjectEvent | null>(null);
    const [runOfShow, setRunOfShow] = useState<AggregatedScheduleItem[]>([]);
    const [rosLoading, setRosLoading] = useState(false);
    const [internalRosFilter, setInternalRosFilter] = useState<string>('ALL');
    const [collapsedEvents, setCollapsedEvents] = useState<ReadonlySet<string>>(new Set());
    const rosEventFilter = externalRosFilter ?? internalRosFilter;
    const onRosTitlesRef = useRef(onRosEventTitlesChange);
    onRosTitlesRef.current = onRosEventTitlesChange;

    // The programme items feed every view — the Gantt plots them, the Run of
    // Show lists them, and the metric cards count them — so they load once on
    // mount rather than only when the Run of Show tab is opened. Programme
    // items live as `tasks` rows (task_type='run_of_show'), fetched in one
    // shared request rather than per event; an item tagged to more than one
    // event (e.g. prep spanning two blocks) is expanded into one row per
    // tagged event so it appears in every relevant group.
    const reload = useCallback(() => {
        setRosLoading(true);
        const projectEvents = loadProjectEvents();
        setEvents(projectEvents);
        const eventById = new Map(projectEvents.map(ev => [ev.id, ev]));
        refreshAllRunOfShowFromApi().then(items => {
            const all: AggregatedScheduleItem[] = [];
            items.forEach(item => {
                (item.eventIds.length > 0 ? item.eventIds : [item.eventId]).forEach(eventId => {
                    const ev = eventById.get(eventId);
                    if (!ev) return; // item tagged to an event that no longer exists
                    all.push({
                        ...item,
                        eventId,
                        eventTitle: ev.title,
                        eventColor: ev.color || EVENT_COLORS[ev.type] || '#94a3b8',
                    });
                });
            });
            all.sort((a, b) => toMinutes(a.time) - toMinutes(b.time));
            setRunOfShow(all);
            const titles = Array.from(new Set(all.map(i => i.eventTitle)));
            onRosTitlesRef.current?.(titles);
            setRosLoading(false);
        });
    }, []);

    useEffect(() => {
        reload();
        window.addEventListener(EVENTS_CHANGED_EVENT, reload);
        return () => window.removeEventListener(EVENTS_CHANGED_EVENT, reload);
    }, [reload]);

    const sorted = [...events].sort((a, b) => a.order - b.order);

    // Programme items grouped by their parent event, in chronological order.
    const itemsByEvent = useMemo(() => {
        const map = new Map<string, AggregatedScheduleItem[]>();
        runOfShow.forEach(item => {
            const bucket = map.get(item.eventId);
            if (bucket) bucket.push(item);
            else map.set(item.eventId, [item]);
        });
        return map;
    }, [runOfShow]);

    // Events shown on the chart, honouring the toolbar's event filter.
    const visibleEvents = useMemo(
        () => sorted.filter(e => rosEventFilter === 'ALL' || e.title === rosEventFilter),
        [sorted, rosEventFilter]
    );

    // Chart window: widest span covering every visible event and its programme
    // items, snapped out to whole hours.
    const chartWindow = useMemo<TimeWindow>(() => {
        const marks: number[] = [];
        visibleEvents.forEach(ev => {
            marks.push(toMinutes(ev.startTime), toEndMinutes(ev.startTime, ev.endTime, 60));
            (itemsByEvent.get(ev.id) ?? []).forEach(item => {
                marks.push(toMinutes(item.time), toEndMinutes(item.time, item.endTime));
            });
        });
        if (marks.length === 0) return { start: 7 * 60, end: MINS_PER_DAY };
        const rawStart = Math.floor(Math.min(...marks) / 60) * 60;
        let rawEnd = Math.ceil(Math.max(...marks) / 60) * 60;
        if (rawEnd - rawStart < MIN_WINDOW_MINS) rawEnd = rawStart + MIN_WINDOW_MINS;
        return { start: rawStart, end: rawEnd };
    }, [visibleEvents, itemsByEvent]);

    const hourTicks = useMemo(() => {
        const ticks: number[] = [];
        for (let m = chartWindow.start; m <= chartWindow.end; m += 60) ticks.push(m);
        return ticks;
    }, [chartWindow]);

    // Categories actually present on the chart, for the legend.
    const legendCategories = useMemo(() => {
        const seen = new Set<string>();
        visibleEvents.forEach(ev =>
            (itemsByEvent.get(ev.id) ?? []).forEach(i => seen.add(i.category))
        );
        return Array.from(seen).sort();
    }, [visibleEvents, itemsByEvent]);

    const toggleEventCollapsed = (id: string) =>
        setCollapsedEvents(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });

    // Run-of-show derived stats
    const nowMins = new Date().getHours() * 60 + new Date().getMinutes();
    const rosElapsed = runOfShow.filter(i => toMinutes(i.time) % MINS_PER_DAY < nowMins).length;
    const rosDurations = runOfShow
      .filter(i => i.endTime)
      .map(i => toEndMinutes(i.time, i.endTime) - toMinutes(i.time))
      .filter(d => d > 0);
    const rosAvgMins = rosDurations.length > 0
      ? Math.round(rosDurations.reduce((s, d) => s + d, 0) / rosDurations.length)
      : 0;
    const rosHighPriority = runOfShow.filter(i => HIGH_PRIORITY_SCHEDULE_CATEGORIES.has(i.category)).length;

    const handleUpdateProgress = (id: string, progress: number) => {
        const updated = events.map(e =>
            e.id === id ? { ...e, progress, status: progress === 100 ? 'completed' : progress > 0 ? 'in-progress' : 'upcoming' as ProjectEvent['status'] } : e
        );
        setEvents(updated);
        saveProjectEvents(updated);
        if (selectedEvent?.id === id) {
            setSelectedEvent(updated.find(e => e.id === id) || null);
        }
    };

    const handleDelete = (id: string) => {
        const updated = events.filter(e => e.id !== id);
        setEvents(updated);
        saveProjectEvents(updated);
        if (selectedEvent?.id === id) setSelectedEvent(null);
    };

    return (
        <div className="wt-container fade-in">
            {/* Stats row */}
            <section className="stats-grid">
                <MetricCard label="Activities" value={runOfShow.length} tone="blue" icon="list" />
                <MetricCard label="Avg Duration" value={rosAvgMins > 0 ? `${rosAvgMins}m` : '—'} tone="teal" icon="clock" />
                <MetricCard label="Elapsed" value={rosElapsed} tone="green" icon="check" />
                <MetricCard label="High Priority" value={rosHighPriority} tone="purple" icon="flag" />
            </section>

            {/* Gantt View */}
            {view === 'gantt' && (
                <div className="wt-gantt-wrapper">
                    <div className="wt-gantt">
                        {/* Time axis header */}
                        <div className="wt-gantt-header">
                            <div className="wt-gantt-label-col">Event / Programme Item</div>
                            <div className="wt-gantt-bars-col">
                                <div className="wt-time-axis">
                                    {hourTicks.map(mins => (
                                        <div
                                            key={mins}
                                            className="wt-time-tick"
                                            style={ { left: `${percentOf(mins, chartWindow)}%` }}
                                        >
                                            {formatHourTick(mins)}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* Grid lines + events */}
                        <div className="wt-gantt-body">
                            {/* Background grid lines */}
                            <div className="wt-grid-lines" aria-hidden="true">
                                <div className="wt-gantt-label-col" />
                                <div className="wt-gantt-bars-col">
                                    {hourTicks.map(mins => (
                                        <div
                                            key={mins}
                                            className="wt-grid-line"
                                            style={ { left: `${percentOf(mins, chartWindow)}%` }}
                                        />
                                    ))}
                                </div>
                            </div>

                            {rosLoading && runOfShow.length === 0 && (
                                <div className="wt-gantt-loading">Loading programme…</div>
                            )}

                            {visibleEvents.map((event, idx) => {
                                const eventStart = toMinutes(event.startTime);
                                const eventEnd = toEndMinutes(event.startTime, event.endTime, 60);
                                const barLeft = percentOf(eventStart, chartWindow);
                                const barWidth = percentOf(eventEnd, chartWindow) - barLeft;
                                const color = event.color || EVENT_COLORS[event.type] || '#94a3b8';
                                const isSelected = selectedEvent?.id === event.id;
                                const items = itemsByEvent.get(event.id) ?? [];
                                const collapsed = collapsedEvents.has(event.id);

                                return (
                                    <React.Fragment key={event.id}>
                                        <div
                                            className={`wt-gantt-row wt-gantt-row--event ${isSelected ? 'selected' : ''}`}
                                            onClick={() => setSelectedEvent(isSelected ? null : event)}
                                        >
                                            <div className="wt-gantt-label-col">
                                                <div className="wt-event-label">
                                                    <button
                                                        type="button"
                                                        className="wt-gantt-toggle"
                                                        aria-expanded={!collapsed}
                                                        aria-label={`${collapsed ? 'Show' : 'Hide'} programme items for ${event.title}`}
                                                        disabled={items.length === 0}
                                                        onClick={e => { e.stopPropagation(); toggleEventCollapsed(event.id); }}
                                                    >
                                                        <Icon name={collapsed ? 'chevronRight' : 'chevronDown'} size={12} />
                                                    </button>
                                                    <span
                                                        className="wt-event-dot"
                                                        style={ { background: color }}
                                                    />
                                                    <span className="wt-event-name">{event.title}</span>
                                                </div>
                                                <div className="wt-event-time-label">
                                                    {formatDisplayTime(event.startTime)} – {formatDisplayTime(event.endTime)}
                                                    {' · '}
                                                    {items.length} {items.length === 1 ? 'item' : 'items'}
                                                </div>
                                                {/* Dependency arrow to next event */}
                                                {idx < visibleEvents.length - 1 && (
                                                    <div className="wt-dep-indicator">
                                                        <Icon name="arrowRight" size={10} />
                                                        <span>{visibleEvents[idx + 1].title}</span>
                                                    </div>
                                                )}
                                            </div>
                                            <div className="wt-gantt-bars-col">
                                                <div
                                                    className="wt-gantt-bar"
                                                    style={ {
                                                        left: `${barLeft}%`,
                                                        width: `${Math.max(barWidth, 2)}%`,
                                                        background: `${color}22`,
                                                        borderLeft: `3px solid ${color}`,
                                                    }}
                                                >
                                                    {/* Progress overlay */}
                                                    <div
                                                        className="wt-bar-progress"
                                                        style={ {
                                                            width: `${event.progress}%`,
                                                            background: color,
                                                        }}
                                                    />
                                                    <span className="wt-bar-label">{event.title}</span>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Programme items inside this event — the actual run of show */}
                                        {!collapsed && items.map(item => {
                                            const itemStart = toMinutes(item.time);
                                            const itemEnd = toEndMinutes(item.time, item.endTime);
                                            const itemLeft = percentOf(itemStart, chartWindow);
                                            const itemWidth = percentOf(itemEnd, chartWindow) - itemLeft;
                                            const catColor = scheduleCategoryColor(item.category);

                                            return (
                                                <div key={item.id} className="wt-gantt-row wt-gantt-row--item">
                                                    <div className="wt-gantt-label-col wt-gantt-label-col--nested">
                                                        <div className="wt-item-label">
                                                            <span className="wt-item-dot" style={ { background: catColor }} />
                                                            <span className="wt-item-name" title={item.activity}>{item.activity}</span>
                                                        </div>
                                                        <div className="wt-item-meta">
                                                            {formatMinutes(itemStart)} – {formatMinutes(itemEnd)}
                                                            {' · '}
                                                            {scheduleCategoryLabel(item.category)}
                                                            {item.assignee ? ` · ${item.assignee}` : ''}
                                                        </div>
                                                    </div>
                                                    <div className="wt-gantt-bars-col">
                                                        <div
                                                            className="wt-gantt-bar wt-gantt-bar--item"
                                                            title={`${item.activity} — ${formatMinutes(itemStart)} to ${formatMinutes(itemEnd)}`}
                                                            style={ {
                                                                left: `${itemLeft}%`,
                                                                width: `${Math.max(itemWidth, 0.8)}%`,
                                                                background: `${catColor}2e`,
                                                                borderLeft: `3px solid ${catColor}`,
                                                            }}
                                                        >
                                                            <span className="wt-bar-label">{item.activity}</span>
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </React.Fragment>
                                );
                            })}
                        </div>
                    </div>

                    {legendCategories.length > 0 && (
                        <div className="wt-legend">
                            {legendCategories.map(cat => (
                                <span key={cat} className="wt-legend-item">
                                    <span
                                        className="wt-legend-dot"
                                        style={ { background: scheduleCategoryColor(cat) }}
                                    />
                                    {scheduleCategoryLabel(cat)}
                                </span>
                            ))}
                        </div>
                    )}

                    </div>
            )}

            {/* Sequence View */}
            {view === 'sequence' && (
                <div className="wt-sequence">
                    {sorted.map((event, idx) => {
                        const color = event.color || EVENT_COLORS[event.type] || '#94a3b8';
                        const isSelected = selectedEvent?.id === event.id;
                        const prereqs = (event.dependsOn || [])
                            .map(id => events.find(e => e.id === id)?.title)
                            .filter(Boolean);

                        return (
                            <div key={event.id} className="wt-sequence-row">
                                {/* Connector line */}
                                {idx < sorted.length - 1 && (
                                    <div className="wt-connector">
                                        <div className="wt-connector-line" style={ { borderColor: color }} />
                                        <div className="wt-connector-arrow">
                                            <Icon name="arrowRight" size={14} />
                                        </div>
                                    </div>
                                )}

                                <div
                                    className={`wt-seq-card ${isSelected ? 'selected' : ''}`}
                                    style={ { borderTopColor: color }}
                                    onClick={() => setSelectedEvent(isSelected ? null : event)}
                                >
                                    <div className="wt-seq-card-header">
                                        <div className="wt-seq-type-badge" style={ { background: `${color}20`, color }}>
                                            {EVENT_LABELS[event.type]}
                                        </div>
                                        <span
                                            className="wt-seq-status"
                                            style={ { color: statusColor[event.status] }}
                                        >
                                            {event.status.replace('-', ' ')}
                                        </span>
                                    </div>

                                    <h3 className="wt-seq-title">{event.title}</h3>

                                    <div className="wt-seq-meta">
                                        <span className="wt-seq-meta-item">
                                            <Icon name="clock" size={13} />
                                            {formatDisplayTime(event.startTime)} – {formatDisplayTime(event.endTime)}
                                        </span>
                                        {event.location && (
                                            <span className="wt-seq-meta-item">
                                                <Icon name="mapPin" size={13} />
                                                {event.location}
                                            </span>
                                        )}
                                        {event.guestCount && (
                                            <span className="wt-seq-meta-item">
                                                <Icon name="users" size={13} />
                                                {event.guestCount} guests
                                            </span>
                                        )}
                                    </div>

                                    <MarkdownText className="wt-seq-desc">{event.description}</MarkdownText>

                                    <div className="wt-seq-progress">
                                        <div className="wt-seq-progress-bar">
                                            <div
                                                className="wt-seq-progress-fill"
                                                style={ { width: `${event.progress}%`, background: color }}
                                            />
                                        </div>
                                        <span className="wt-seq-progress-label">{event.progress}% planned</span>
                                    </div>

                                    {prereqs.length > 0 && (
                                        <div className="wt-seq-deps">
                                            <Icon name="arrowRight" size={12} />
                                            Follows: {prereqs.join(', ')}
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Run of Show View */}
            {view === 'run-of-show' && (
                <div className="wt-ros">
                    {rosLoading ? (
                        <div className="wt-ros-loading">Loading schedule…</div>
                    ) : runOfShow.length === 0 ? (
                        <div className="wt-ros-empty">No schedule items found. Open an event and seed its Run of Show first.</div>
                    ) : (
                        <div className="wt-ros-list">
                            {runOfShow.filter(item => rosEventFilter === 'ALL' || item.eventTitle === rosEventFilter).map((item, idx, filtered) => {
                                const catColor = scheduleCategoryColor(item.category);
                                const prevItem = idx > 0 ? filtered[idx - 1] : null;
                                const showEventBanner = !prevItem || prevItem.eventTitle !== item.eventTitle;
                                return (
                                    <React.Fragment key={item.id}>
                                        {showEventBanner && (
                                            <div className="wt-ros-event-banner" style={{ /* ds-dynamic: event color from data */ borderLeftColor: item.eventColor }}>
                                                <span className="wt-ros-event-dot" style={{ /* ds-dynamic: event color from data */ background: item.eventColor }} />
                                                {item.eventTitle}
                                            </div>
                                        )}
                                        <div className="wt-ros-row">
                                            <div className="wt-ros-time">
                                                <span className="wt-ros-time-start">{formatDisplayTime(item.time)}</span>
                                                {item.endTime && (
                                                    <span className="wt-ros-time-end">→ {formatDisplayTime(item.endTime)}</span>
                                                )}
                                            </div>
                                            <div className="wt-ros-connector">
                                                <div className="wt-ros-dot" style={{ /* ds-dynamic: category color from map */ background: catColor }} />
                                                {idx < filtered.length - 1 && <div className="wt-ros-line" />}
                                            </div>
                                            <div className="wt-ros-content">
                                                <div className="wt-ros-activity">{item.activity}</div>
                                                <div className="wt-ros-meta">
                                                    <StatusBadge color={catColor}>
                                                        {scheduleCategoryLabel(item.category)}
                                                    </StatusBadge>
                                                    {item.assignee && (
                                                        <span className="wt-ros-assignee">
                                                            <Icon name="users" size={11} /> {item.assignee}
                                                        </span>
                                                    )}
                                                </div>
                                                {item.notes && (
                                                    <div className="wt-ros-notes">{item.notes}</div>
                                                )}
                                            </div>
                                        </div>
                                    </React.Fragment>
                                );
                            })}
                        </div>
                    )}
                </div>
            )}

            {/* Detail Panel */}
            {selectedEvent && (
                <div className="wt-detail-panel">
                    <div className="wt-detail-header">
                        <h3>{selectedEvent.title}</h3>
                        <button className="wt-detail-close" onClick={() => setSelectedEvent(null)}>
                            <Icon name="close" size={16} />
                        </button>
                    </div>

                    <div className="wt-detail-body">
                        <div
                            className="wt-detail-type-badge"
                            style={ {
                                background: `${(selectedEvent.color || EVENT_COLORS[selectedEvent.type])}20`,
                                color: selectedEvent.color || EVENT_COLORS[selectedEvent.type],
                            }}
                        >
                            {EVENT_LABELS[selectedEvent.type]}
                        </div>

                        <div className="wt-detail-row">
                            <Icon name="clock" size={14} />
                            <span>{formatDisplayTime(selectedEvent.startTime)} – {formatDisplayTime(selectedEvent.endTime)}</span>
                        </div>
                        {selectedEvent.location && (
                            <div className="wt-detail-row">
                                <Icon name="mapPin" size={14} />
                                <span>{selectedEvent.location}</span>
                            </div>
                        )}
                        {selectedEvent.guestCount && (
                            <div className="wt-detail-row">
                                <Icon name="users" size={14} />
                                <span>{selectedEvent.guestCount} guests</span>
                            </div>
                        )}
                        <MarkdownText className="wt-detail-desc">{selectedEvent.description}</MarkdownText>

                        <div className="wt-detail-progress">
                            <div className="wt-detail-progress-label">
                                <span>Planning Progress</span>
                                <span>{selectedEvent.progress}%</span>
                            </div>
                            <div className="wt-progress-bar-track">
                                <div
                                    className="wt-progress-bar-fill"
                                    style={{ /* ds-dynamic: progress percentage from data */ width: `${selectedEvent.progress}%` }}
                                />
                            </div>
                        </div>

                        {(selectedEvent.dependsOn || []).length > 0 && (
                            <div className="wt-detail-deps">
                                <strong>Follows:</strong>{' '}
                                {(selectedEvent.dependsOn || [])
                                    .map(id => events.find(e => e.id === id)?.title)
                                    .filter(Boolean)
                                    .join(', ')}
                            </div>
                        )}

                    </div>
                </div>
            )}

        </div>
    );
};

export default ProjectTimeline;
